import React from "react";
import { useTranslation } from "react-i18next";
import AppIcon, { AppIconType } from "../../Core/AppIcon/AppIcon";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { cn } from "../../../utils/cn";

/** One entry in the list toolbar's "..." overflow menu. */
export interface ListToolbarAction {
	/** Menu item label (i18n key). */
	label: string;
	/** Invoked when the item is clicked (the menu closes afterwards). */
	onClick: () => void;
	/** Optional leading icon. */
	icon?: AppIconType;
	disabled?: boolean;
	/** Renders the item in the danger color (for destructive actions). */
	danger?: boolean;
	/** Draws a separator above this item (e.g. before a destructive action). */
	dividerBefore?: boolean;
}

interface ListActionsMenuProps {
	actions: ListToolbarAction[];
	/**
	 * Extra menu content (e.g. a {@link TagToggleSubMenu}) rendered between the
	 * regular actions and the first `dividerBefore` action, so a destructive
	 * tail stays last. Must be DropdownMenu primitives.
	 */
	extraItems?: React.ReactNode;
}

/**
 * The "..." overflow button for list/detail page toolbars — a bordered square
 * button matching the filter button, opening a shadcn DropdownMenu of
 * {@link ListToolbarAction}s. Being a real DropdownMenu it can also host
 * submenus etc via `extraItems`.
 */
const ListActionsMenu: React.FC<ListActionsMenuProps> = ({ actions, extraItems }) => {
	const { t } = useTranslation();

	const firstDividerIndex = actions.findIndex((action) => action.dividerBefore);
	const splitIndex = firstDividerIndex === -1 ? actions.length : firstDividerIndex;

	const renderAction = (action: ListToolbarAction, index: number) => (
		<React.Fragment key={index}>
			{action.dividerBefore && <DropdownMenuSeparator />}
			<DropdownMenuItem
				disabled={action.disabled}
				onSelect={() => action.onClick()}
				className={cn(action.danger && !action.disabled && "text-danger focus:text-danger")}
			>
				{action.icon && <AppIcon icon={action.icon} margin="0" />}
				<span>{t(action.label as any)}</span>
			</DropdownMenuItem>
		</React.Fragment>
	);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className="flex h-[33px] w-10 items-center justify-center rounded-md border border-solid border-border bg-white hover:border-action"
					title={t("MORE" as any)}
				>
					<AppIcon icon="menu-dots" margin="0" />
				</button>
			</DropdownMenuTrigger>

			<DropdownMenuContent align="end" className="min-w-[200px]">
				{actions.slice(0, splitIndex).map(renderAction)}
				{extraItems}
				{actions.slice(splitIndex).map((action, index) => renderAction(action, splitIndex + index))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default ListActionsMenu;
